import { useMemo } from 'react';
import useRetryPresentation from '@/hooks/api/stepExecutions/useRetryPresentation.ts';
import { buildRetryAttemptItems } from '@/pages/Run/components/RetryAttemptItems.ts';
import {
  asChildScopeChoices,
  buildChildScopeItems,
  type ChildScopeChoice,
  type ChildScopeContext,
  getChildScopeChoiceId,
  getChildScopeParentSteps,
} from '@/pages/Run/components/ScopeStack/childScopeModel.ts';
import { getStepExecutionChildScopeState } from '@/types/stepExecutions.ts';
import { StepType } from '@/types/automationFields.ts';
import { AutomationStep } from '@/types/automationSteps.ts';

type Args = {
  context: ChildScopeContext;
  stepsById: Record<string, AutomationStep>;
  enabled: boolean;
};

type Result = {
  items: ChildScopeChoice[];
  defaultChoiceId: string | null;
  isRetryPresentationPending: boolean;
  isRetryPresentationError: boolean;
};

export const useChildScopeChoices = ({ context, stepsById, enabled }: Args): Result => {
  const parentSteps = useMemo(() => getChildScopeParentSteps(context), [context]);
  const childScopeState = getStepExecutionChildScopeState(context.stepExecution);
  const isRetry = context.stepRun.step_type === StepType.Retry;
  const {
    data: retryPresentation,
    isPending: isRetryPresentationPending,
    isError: isRetryPresentationError,
  } = useRetryPresentation(context.stepRun.id, enabled && isRetry);
  const { items: retryItems, latestItemValue } = useMemo(
    () => buildRetryAttemptItems({ presentation: retryPresentation }),
    [retryPresentation],
  );

  return useMemo(() => {
    if (isRetry) {
      const items = asChildScopeChoices(retryItems);
      const latestItem = items.find((item) => item.value === latestItemValue) ?? items.at(-1);
      return {
        items,
        defaultChoiceId: latestItem ? getChildScopeChoiceId(latestItem) : null,
        isRetryPresentationPending,
        isRetryPresentationError,
      };
    }
    const items = childScopeState
      ? buildChildScopeItems(childScopeState, parentSteps, stepsById)
      : [];
    return {
      items,
      defaultChoiceId: items[0] ? getChildScopeChoiceId(items[0]) : null,
      isRetryPresentationPending: false,
      isRetryPresentationError: false,
    };
  }, [
    childScopeState,
    isRetry,
    isRetryPresentationError,
    isRetryPresentationPending,
    latestItemValue,
    parentSteps,
    retryItems,
    stepsById,
  ]);
};
